<template>
  <v-flex>
    <!--
      Every input field will contain attribute autocomplete to bust Chrome autofill,
      Chrome doesn't obey autocomplete="off", bastards.

      https://bugs.chromium.org/p/chromium/issues/detail?id=468153
    -->
    <v-text-field
      label="Address 1"
      v-model="workingAddress.StreetAddress1"
      required
      :rules="[validationRules.required]"
      @change="addressFormChange"
      :autocomplete="`${Date.now()}`"
    ></v-text-field>
    <v-text-field
      label="Address 2"
      v-model="workingAddress.StreetAddress2"
      required
      @change="addressFormChange"
      :autocomplete="`${Date.now()}`"
    ></v-text-field>
    <v-layout>
      <v-flex md6 pr-3>
        <v-autocomplete
          label="Country"
          :items="countryList"
          item-text="CountryName"
          item-value="CountryName"
          return-object
          required
          v-model="selectedCountry"
          :rules="[validationRules.required]"
          :autocomplete="`${Date.now()}`"
        >
          <template v-slot:no-data>
            <div class="countryRegionCityLoading">
              Loading countries...
              <v-progress-circular indeterminate color="gray"></v-progress-circular>
            </div>
          </template>

          <template v-slot:item="data">
            <!-- display loading message when immediately display saved country
            and the actual countries list is not ready yet -->
            <!-- check list lengths less than 2 to detect when the actual countries list is not ready
            and there is only saved country in the coutries list -->
            <template v-if="countryList.length < 2">
              <div class="countryRegionCityLoading">
                Loading countries...
                <v-progress-circular indeterminate color="gray"></v-progress-circular>
              </div>
            </template>
            <template v-else>
              {{ data.item.CountryName }}
            </template>
          </template>
        </v-autocomplete>
      </v-flex>
      <v-flex md6 pl-3>
        <v-autocomplete
          label="State/Province"
          :disabled="!selectedCountry"
          :items="regionList"
          item-text="RegionName"
          item-value="RegionName"
          return-object
          required
          v-model="selectedRegion"
          :rules="[validationRules.required]"
          :autocomplete="`${Date.now()}`"
        >
          <template v-slot:no-data>
            <div class="countryRegionCityLoading">
              Loading regions...
              <v-progress-circular indeterminate color="gray"></v-progress-circular>
            </div>
          </template>

          <template v-slot:item="data">
            <!-- display loading message when immediately display saved region
            and the actual regions list is not ready yet -->
            <!-- check list lengths less than 2 to detect when the actual regions list is not ready
            and there is only saved region in the regions list -->
            <template v-if="regionList.length < 2">
              <div class="countryRegionCityLoading">
                Loading regions...
                <v-progress-circular indeterminate color="gray"></v-progress-circular>
              </div>
            </template>
            <template v-else>
              {{ data.item.RegionName }}
            </template>
          </template>
        </v-autocomplete>
      </v-flex>
    </v-layout>
    <v-layout>
      <v-flex md6 pr-3>
        <v-autocomplete
          label="City"
          :disabled="!selectedRegion"
          :items="cityList"
          item-text="CityName"
          item-value="CityName"
          return-object
          required
          v-model="selectedCity"
          @change="isCityChanged = true"
          :rules="[validationRules.required]"
          :autocomplete="`${Date.now()}`"
        >
          <template v-slot:no-data>
            <div class="countryRegionCityLoading">
              Loading cities...
              <v-progress-circular indeterminate color="gray"></v-progress-circular>
            </div>
          </template>

          <template v-slot:item="data">
            <!-- display loading message when immediately display saved city
            and the actual cities list is not ready yet -->
            <!-- check list lengths less than 2 to detect when the actual cities list is not ready
            and there is only saved city in the cities list -->
            <template v-if="cityList.length < 2">
              <div class="countryRegionCityLoading">
                Loading cities...
                <v-progress-circular indeterminate color="gray"></v-progress-circular>
              </div>
            </template>
            <template v-else>
              {{ data.item.CityName }}
            </template>
          </template>
        </v-autocomplete>
      </v-flex>
      <v-flex md6 pl-3>
        <v-text-field
          label="Zip/Postal Code"
          v-model="workingAddress.ZipPostal"
          required
          :rules="[validationRules.required]"
          @change="addressFormChange"
          :autocomplete="`${Date.now()}`"
        ></v-text-field>
      </v-flex>
    </v-layout>
  </v-flex>
</template>

<script>
export default {
  name: 'AddressForm',
  props: ['address'], // We cannot predefine address only a object as legacy code has this as a string... need to handle both
  data() {
    return {
      isCityChanged: false,

      workingAddress: {
        StreetAddress1: '',
        StreetAddress2: '',
        Country: '',
        StateProvince: '',
        City: '',
        ZipPostal: '',
        TimeZone: '',
      },
      workingAddressWatcher: undefined,

      countryList: [],
      selectedCountry: undefined,
      selectedCountryWatcher: undefined,
      isAutosetRegionsWhenRegionsLoaded: false, // callback flag for prepopulating address

      regionList: [],
      selectedRegion: undefined,
      selectedRegionWatcher: undefined,
      isAutosetCitiesWhenCitiesLoaded: false, // callback flag for prepopulating address

      cityList: [],
      selectedCity: undefined,
      selectedCityWatcher: undefined,

      validationRules: {
        required: value => !!value || 'Required',
      },
    }
  },
  computed: {
    EnvService() {
      return window.adacado.bscs.EnvService
    },
  },
  beforeDestroy() {
    // Stop all watchers lest we create memory leaks
    if (this.workingAddressWatcher) this.workingAddressWatcher()
    if (this.selectedCountryWatcher) this.selectedCountryWatcher()
    if (this.selectedRegionWatcher) this.selectedRegionWatcher()
    if (this.selectedCityWatcher) this.selectedCityWatcher()
  },
  async beforeMount() {
    // ORDER OF OPERATIONS IS IMPORTANT HERE, TO AVOID RACE CONDITIONS

    // 1) PRELOAD SAVED DSP CONFIG IF PRESENT, ELSE SET DEFAULTS
    // Backwards compatibility note - addresses used to be single string fields, but now we work with objects and
    // will force users to re-enter old invalid string addresses, ie old string address will not be loaded
    if (typeof this.address === 'object' && Object.keys(this.address).length) {
      this.workingAddress = { ...this.address }
      // display saved country, region and city immediatly without loading their lists.
      const savedCountry = { CountryName: this.workingAddress.Country }
      const savedRegion = { RegionName: this.workingAddress.StateProvince }
      const savedCity = { CityName: this.workingAddress.City }

      this.countryList = [savedCountry]
      this.selectedCountry = savedCountry

      this.regionList = [savedRegion]
      this.selectedRegion = savedRegion

      this.cityList = [savedCity]
      this.selectedCity = savedCity
    }

    // 2) FETCH COUNTRY LIST BEFORE DOING ANYTHING ELSE
    this.countryList = await fetch(
      `https://${
        this.EnvService.IS_PRODUCTION_CONNECTION ? '' : 'staging-'
      }cdn-wsa.adacado.com/geo/v1/getcountries/billing`
    )
      .then(r => r.json())
      .then(r => {
        // Move US & Canada to top of list
        return _moveToFront(_moveToFront(r, 'United States'), 'Canada')
        function _moveToFront(arr, countryName) {
          return [
            ...arr.splice(
              arr.findIndex(e => e.CountryName === countryName),
              1
            ),
            ...arr,
          ]
        }
      })

    // 3) SETUP WATCHERS THAT DYNAMICALLY REGENERATE REGION/CITY LISTS BASED ON PREVIOUS SELECTIONS
    // When country changes, update list of regions, update working address
    this.selectedCountryWatcher = this.$watch('selectedCountry', async function(c) {
      if (!c) return

      // Update dirty data set
      this.workingAddress.Country = c.CountryName

      // Clear out selected regionList, cityList whose country is no longer selected
      if (!this.isAutosetRegionsWhenRegionsLoaded) {
        this.regionList = undefined
        this.cityList = undefined
      }

      // Get list of regions
      await fetch(
        `https://${
          this.EnvService.IS_PRODUCTION_CONNECTION ? '' : 'staging-'
        }cdn-wsa.adacado.com/geo/v1/getregionsbycountrycode/${c.CountryCode}/billing`
      )
        .then(async res => await res.json())
        .then(res => {
          this.regionList = res
            .map(r => {
              r.cartography = {
                CountryCode: c.CountryCode,
                CountryName: c.CountryName,
              }
              return r
            })
            .sort((a, b) => {
              const x = a.RegionName.toLowerCase()
              const y = b.RegionName.toLowerCase()
              return x < y ? -1 : x > y ? 1 : 0
            })
        })

      // Clear out selected regions whose country is no longer selected
      if (!this.isAutosetRegionsWhenRegionsLoaded) {
        this.selectedRegion = undefined
        this.selectedCity = undefined
      }

      // Callback to set previously saved region
      if (this.isAutosetRegionsWhenRegionsLoaded) {
        this.populateRegion()
      }
    })

    // When region changes, update list of cities, update working address
    this.selectedRegionWatcher = this.$watch('selectedRegion', async function(c) {
      if (!c) return

      // Update dirty data set
      this.workingAddress.StateProvince = c.RegionName

      // Clear out selected cityList whose region is no longer selected
      if (!this.isAutosetCitiesWhenCitiesLoaded) this.cityList = undefined

      // Get list of cities
      await fetch(
        `https://${
          this.EnvService.IS_PRODUCTION_CONNECTION ? '' : 'staging-'
        }cdn-wsa.adacado.com/geo/v1/getcitiesbyregioncode/${c.cartography.CountryCode}/${c.RegionCode}/billing`
      )
        .then(async res => await res.json())
        .then(res => {
          this.cityList = res
            .map(r => {
              r.cartography = {
                CountryCode: c.cartography.CountryCode,
                CountryName: c.cartography.CountryName,
                RegionCode: c.RegionCode,
                RegionName: c.RegionName,
              }
              return r
            })
            .sort((a, b) => {
              const x = a.CityName.toLowerCase()
              const y = b.CityName.toLowerCase()
              return x < y ? -1 : x > y ? 1 : 0
            })
        })

      // Clear out selected cities whose region is no longer selected
      if (!this.isAutosetCitiesWhenCitiesLoaded) this.selectedCity = undefined

      // Callback to set previously saved city
      if (this.isAutosetCitiesWhenCitiesLoaded) {
        this.populateCity()
      }
    })

    // When city changes, update working address
    this.selectedCityWatcher = this.$watch('selectedCity', async function(c) {
      this.workingAddress.City = (c && c.CityName) || undefined
      this.workingAddress.TimeZone = (c && c.timezone) || undefined

      // update Country, Region and City after City field changed
      if (this.isCityChanged) {
        this.$emit('addressFormChanged', this.workingAddress)
        this.isCityChanged = false
      }
    })

    // 4) LOAD REGIONS LIST AND CITIES LIST FOR PERSISTED ADDRESS
    if (typeof this.address === 'object' && Object.keys(this.address).length) {
      this.populateCountry()
    }
  },
  methods: {
    // Callback functions for prepopulating Country/Region/City from previously saved data
    // It is possible that old address data does not fit nicely with ISO Country/Region/City lists,
    // in which case just skip and move on

    // Populate country field from loaded address
    populateCountry() {
      this.isAutosetRegionsWhenRegionsLoaded = true
      const { Country = '' } = this.workingAddress
      this.selectedCountry = this.countryList.find(
        el => Country.toLowerCase() == el.CountryName.toLowerCase()
      )
    },
    // Populate state/province field from loaded address
    populateRegion() {
      this.isAutosetRegionsWhenRegionsLoaded = false
      this.isAutosetCitiesWhenCitiesLoaded = true
      const { StateProvince = '' } = this.workingAddress
      this.selectedRegion = this.regionList.find(
        el => StateProvince.toLowerCase() == el.RegionName.toLowerCase()
      )
    },
    // Populate city field from loaded address
    populateCity() {
      this.isAutosetCitiesWhenCitiesLoaded = false
      const { City = '' } = this.workingAddress
      this.selectedCity = this.cityList.find(el => City.toLowerCase() == el.CityName.toLowerCase())
    },
    addressFormChange() {
      // update new address info (exclude Country, Region, City)
      this.$emit('addressFormChanged', this.workingAddress)
    },
  },
}
</script>

<style scoped>
.countryRegionCityLoading {
  height: 35px;
  overflow: hidden;
  text-align: center;
}
</style>
